import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import Btn from '../../../../../components/Buttons/Btn';
import { backendErrorNotification } from '../../../../../components/Notification';
import TablePaginator from '../../../../../components/TablePaginator';
import {
  ActionButtonsContainer,
  DeleteIconTable,
} from '../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../enums/httpStatus';
import i18n from '../../../../../i18n';
import { communicationByActionAndOrderReq } from '../../../../../services/requests';
import * as TP from '../../../../../types/apiResponseTypes';
import Loader from './Loader';

import NoData from '../../../../../components/NoData';
import DeleteCommunicationByActionAndOrder from './Components/DeleteCommunicationByActionAndOrder';
import LinkCommunicationByActionAndOrder from './Components/LinkCommunicationByActionAndOrder';

const CommunicationByActionAndOrder = () => {
  const { t } = useTranslation();

  const [waitingGetCommunications, setWaitingGetCommunications] = useState(false);
  const [communications, setCommunications] = useState<TP.CommunicationByActionAndOrder[] | []>([]);
  const [itemSelected, setItemSelected] = useState<TP.CommunicationByActionAndOrder | null>(null);

  const [showModalLink, setShowModalLink] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);

  const reqGetCommunicationByActionAndOrder = useCallback(async () => {
    setWaitingGetCommunications(true);

    const source = communicationByActionAndOrderReq.axios.CancelToken.source();

    try {
      const res = await communicationByActionAndOrderReq.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data }: { data: TP.CommunicationByActionAndOrder[] | [] } = res.data;
        setCommunications(data);
      } else {
        setCommunications([]);
        throw res;
      }

      setWaitingGetCommunications(false);
    } catch (err) {
      if (!communicationByActionAndOrderReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setWaitingGetCommunications(false);
    }

    return () => {
      source.cancel('Component Integrations Logs got unmounted');
    };
  }, []);

  useEffect(() => {
    reqGetCommunicationByActionAndOrder();
  }, []);

  const handleTableDeleteBtn = (row: Row<TP.CommunicationByActionAndOrder>) => {
    setItemSelected(row.original);
    setShowModalDelete(true);
  };

  const actionsButtons = (row: Row<TP.CommunicationByActionAndOrder>) => (
    <ActionButtonsContainer>
      <DeleteIconTable onClick={() => handleTableDeleteBtn(row)} />
    </ActionButtonsContainer>
  );

  const columns: Column<TP.CommunicationByActionAndOrder>[] = useMemo(
    () => [
      {
        Header: t('motive') as string,
        accessor: 'motive',
        Cell: (cell: any) => cell.value.description,
      },
      {
        Header: t('action') as string,
        accessor: 'action',
        Cell: (cell: any) => cell.value.label,
      },
      {
        Header: t('origin') as string,
        accessor: 'order_origin',
        Cell: (cell: any) => cell.value.label,
      },
      {
        Header: t('communicationGroup') as string,
        accessor: 'communication_group',
        Cell: (cell: any) => cell.value.description,
      },
      {
        id: 'actions',
        Cell: ({ row }: any) => actionsButtons(row),
      },
    ],
    [i18n.language],
  );

  return (
    <>
      <Btn
        text={t('bind')}
        onClick={() => setShowModalLink(true)}
        disabled={waitingGetCommunications}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showModalLink && (
        <LinkCommunicationByActionAndOrder
          closeModal={() => setShowModalLink(false)}
          reqGetAllCommunicationByActionAndOrder={() => reqGetCommunicationByActionAndOrder()}
        />
      )}

      {showModalDelete && (
        <DeleteCommunicationByActionAndOrder
          itemSelected={itemSelected as TP.CommunicationByActionAndOrder}
          closeModal={() => setShowModalDelete(false)}
          reqGetAllCommunicationByActionAndOrder={() => reqGetCommunicationByActionAndOrder()}
        />
      )}

      <Loader isLoading={waitingGetCommunications}>
        {communications.length >= 1 ? (
          <TablePaginator data={communications} columns={columns} globalFiltering />
        ) : (
          <NoData />
        )}
      </Loader>
    </>
  );
};

export default CommunicationByActionAndOrder;
